import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Download, ArrowRight, Sparkles, Target, Compass, Heart } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import Layout from '@/components/Layout';
import ProgressCircle from '@/components/ProgressCircle';
import CategoryBadge from '@/components/CategoryBadge';
import { Button } from '@/components/ui/button';
import { PILLARS, pillarTitle } from '@/constants/pillars';
import { SCORE } from '@/constants/testIds';

export default function ScoreResult() {
	const { id } = useParams();
	const [doc, setDoc] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		(async () => {
			try {
				const { data } = await api.get('/assessments');
				const list = data.assessments || [];
				const found = list.find((a) => a.assessment_id === id) || list[0];
				setDoc(found || null);
			} catch (err) {
				toast.error(formatApiError(err));
			} finally {
				setLoading(false);
			}
		})();
	}, [id]);

	const downloadHtml = () => {
		if (!doc) return;
		const date = new Date(doc.created_at).toLocaleDateString();
		const pillarRows = Object.entries(doc.pillars || {})
			.map(([k, v]) => `<tr><td style="padding:8px 12px;border-bottom:1px solid #E2E8F0">${pillarTitle(k)}</td><td style="padding:8px 12px;border-bottom:1px solid #E2E8F0;text-align:right;font-weight:700">${v}</td></tr>`)
			.join('');
		const planItems = (doc.coach?.improvement_plan || []).map((s) => `<li style="margin:6px 0">${s}</li>`).join('');
		const html = `<!doctype html><html><head><meta charset="utf-8"><title>MyFamShield Resilience Report</title>
			<style>
				body{font-family:Manrope,system-ui,sans-serif;background:#F8FAFC;color:#0F172A;margin:0;padding:40px}
				h1,h2,h3{font-family:Outfit,system-ui,sans-serif;color:#0F172A}
				.wrap{max-width:760px;margin:0 auto;background:#fff;border-radius:24px;padding:40px;box-shadow:0 8px 32px rgba(15,23,42,.06)}
				.score{font-size:64px;font-weight:800;color:#1E3A8A}
				.pill{display:inline-block;padding:4px 12px;border-radius:999px;background:#D1FAE5;color:#065F46;font-weight:700;font-size:12px;letter-spacing:.1em;text-transform:uppercase}
				.small{color:#64748B;font-size:12px}
				table{width:100%;border-collapse:collapse;margin-top:8px}
				.section{margin-top:28px}
				.quote{padding:16px 20px;border-left:4px solid #10B981;background:#ECFDF5;border-radius:12px;color:#065F46}
			</style></head><body>
			<div class="wrap">
				<div style="display:flex;justify-content:space-between;align-items:center">
					<div>
						<div style="font-weight:800;font-family:Outfit">MyFamShield</div>
						<div class="small">Family Resilience Report — ${date}</div>
					</div>
					<div class="pill">${doc.category}</div>
				</div>
				<div style="display:flex;gap:32px;align-items:center;margin-top:28px">
					<div class="score">${doc.overall}</div>
					<div class="small">Family Resilience Score (0–100)<br/>Higher means more prepared.</div>
				</div>
				<div class="section">
					<h3>Coach Summary</h3>
					<p>${doc.coach?.summary || ''}</p>
				</div>
				<div class="section">
					<h3>Monthly Mission</h3>
					<div class="quote">${doc.coach?.monthly_mission || ''}</div>
				</div>
				<div class="section">
					<h3>Improvement Plan</h3>
					<ol>${planItems}</ol>
				</div>
				<div class="section">
					<h3>Pillar Breakdown</h3>
					<table><tbody>${pillarRows}</tbody></table>
				</div>
				<div class="section quote">
					${doc.coach?.motivation || ''}
				</div>
				<div class="small" style="margin-top:32px">MyFamShield is a Family Preparedness Platform. It is not a financial advisor, investment advisor, or insurance marketplace.</div>
			</div>
			</body></html>`;
		const blob = new Blob([html], { type: 'text/html' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `MyFamShield-Resilience-Report-${date.replace(/\//g, '-')}.html`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		setTimeout(() => URL.revokeObjectURL(url), 1000);
	};

	if (loading) {
		return (
			<Layout>
				<div className="card-soft p-10 text-center text-slate-500">Loading your report…</div>
			</Layout>
		);
	}

	if (!doc) {
		return (
			<Layout>
				<div className="card-soft p-10 text-center">
					<p className="text-slate-600">No assessment yet.</p>
					<Link to="/assessment"><Button className="mt-4 rounded-full">Take your first assessment</Button></Link>
				</div>
			</Layout>
		);
	}

	return (
		<Layout>
			<div data-testid={SCORE.root} className="enter-stagger space-y-8">
				<div className="card-soft p-8 sm:p-10 flex flex-col lg:flex-row items-center gap-10">
					<ProgressCircle value={doc.overall} sublabel={doc.category} label="Family Resilience Score" size={260} stroke={20} />
					<div className="flex-1">
						<CategoryBadge category={doc.category} />
						<h1 className="mt-4 text-3xl sm:text-4xl font-heading font-bold tracking-tight text-slate-900">
							Your Family Resilience Score is <span className="text-blue-800">{doc.overall}</span>
						</h1>
						<p className="mt-4 text-slate-600 leading-relaxed">{doc.coach?.summary}</p>
						<div className="mt-6 flex flex-wrap gap-3">
							<Button onClick={downloadHtml} className="rounded-full gap-2" data-testid={SCORE.downloadButton}>
								<Download className="w-4 h-4" /> Download report
							</Button>
							<Link to="/dashboard">
								<Button variant="outline" className="rounded-full" data-testid={SCORE.backToDashboard}>
									Back to dashboard <ArrowRight className="w-4 h-4 ml-2" />
								</Button>
							</Link>
						</div>
					</div>
				</div>

				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
					<div className="card-soft p-7">
						<div className="flex items-center gap-2 text-emerald-700">
							<Target className="w-4 h-4" />
							<span className="text-xs font-bold uppercase tracking-[0.2em]">Monthly Mission</span>
						</div>
						<h3 className="mt-3 text-xl font-heading font-semibold text-slate-900">{doc.coach?.monthly_mission}</h3>
					</div>
					<div className="card-soft p-7">
						<div className="flex items-center gap-2 text-blue-800">
							<Compass className="w-4 h-4" />
							<span className="text-xs font-bold uppercase tracking-[0.2em]">Top Priority</span>
						</div>
						<h3 className="mt-3 text-xl font-heading font-semibold text-slate-900">{doc.coach?.top_priority}</h3>
						<p className="mt-2 text-slate-600 text-sm">{doc.coach?.next_goal}</p>
					</div>
					<div className="card-soft p-7">
						<div className="flex items-center gap-2 text-amber-600">
							<Heart className="w-4 h-4" />
							<span className="text-xs font-bold uppercase tracking-[0.2em]">Motivation</span>
						</div>
						<h3 className="mt-3 text-lg font-heading font-semibold text-slate-900 leading-relaxed">{doc.coach?.motivation}</h3>
					</div>
				</div>

				<div className="card-soft p-7">
					<div className="flex items-center gap-2 text-emerald-700">
						<Sparkles className="w-4 h-4" />
						<span className="text-xs font-bold uppercase tracking-[0.2em]">Improvement Plan</span>
					</div>
					<ol className="mt-4 space-y-3">
						{(doc.coach?.improvement_plan || []).map((s, i) => (
							<li key={i} className="flex items-start gap-3">
								<span className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 font-bold grid place-items-center text-sm shrink-0">{i + 1}</span>
								<span className="text-slate-700 leading-relaxed">{s}</span>
							</li>
						))}
					</ol>
				</div>

				<div>
					<h2 className="text-2xl font-heading font-semibold text-slate-900 mb-4">Pillar Breakdown</h2>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
						{PILLARS.map((p) => {
							const v = doc.pillars[p.key] ?? 0;
							const Icon = p.icon;
							return (
								<div key={p.key} className="card-soft p-6">
									<div className="flex items-center gap-3">
										<div className={`w-10 h-10 rounded-2xl bg-gradient-to-br ${p.accent} text-white grid place-items-center`}>
											<Icon className="w-5 h-5" />
										</div>
										<div className="font-heading font-semibold text-slate-900">{p.title}</div>
										<div className="ml-auto text-2xl font-heading font-bold text-slate-900 tabular-nums">{v}</div>
									</div>
									<div className="mt-4 h-2 rounded-full bg-slate-100 overflow-hidden">
										<div className="h-full rounded-full bg-gradient-to-r from-blue-700 to-emerald-500 transition-[width] duration-700" style={{ width: `${v}%` }} />
									</div>
									<p className="mt-3 text-sm text-slate-600">{p.blurb}</p>
								</div>
							);
						})}
					</div>
				</div>
			</div>
		</Layout>
	);
}
